import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
    ChevronLeft, 
    ChevronRight, 
    Plus, 
    HardHat,
    LogOut,
    LayoutGrid,
    Clipboard,
    Megaphone,
    Target,
    CalendarDays,
    Filter,
    X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { postsAPI } from '../lib/api';
import { toast } from 'sonner';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const DIAS_SHORT = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];

const REDES = [
    { value: 'instagram', label: 'Instagram', solid: 'bg-pink-500' },
    { value: 'facebook', label: 'Facebook', solid: 'bg-blue-600' },
    { value: 'linkedin', label: 'LinkedIn', solid: 'bg-blue-700' },
    { value: 'tiktok', label: 'TikTok', solid: 'bg-slate-900' },
    { value: 'twitter', label: 'Twitter/X', solid: 'bg-slate-800' },
    { value: 'multiple', label: 'Múltiples', solid: 'bg-purple-600' }
];

const ESTADOS = [
    { value: 'borrador', label: 'Borrador', color: 'bg-slate-100 text-slate-600 border-slate-300' },
    { value: 'pendiente', label: 'Pendiente', color: 'bg-amber-50 text-amber-700 border-amber-300' },
    { value: 'aprobado', label: 'Aprobado', color: 'bg-emerald-50 text-emerald-700 border-emerald-300' },
    { value: 'publicado', label: 'Publicado', color: 'bg-blue-50 text-blue-700 border-blue-300' }
];

const getRedConfig = (red) => REDES.find(r => r.value === red) || REDES[0];
const getEstadoConfig = (estado) => ESTADOS.find(e => e.value === estado) || ESTADOS[0];

const formatDate = (date) => date.toISOString().split('T')[0];

const getMonthDays = (year, month) => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const days = [];
    
    // Get first day of week (0 = Sunday, we want Monday = 0)
    let startDayOfWeek = firstDay.getDay();
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;
    
    // Add days from previous month
    for (let i = startDayOfWeek - 1; i >= 0; i--) {
        const d = new Date(year, month, -i);
        days.push({ date: d, isCurrentMonth: false });
    }
    
    // Add days of current month
    for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    // Add days from next month to complete the grid (6 rows)
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
        days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
};

const GrillaMensualPage = () => {
    const { user, logout, isAdmin } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
    const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
    
    // Filter states
    const [filterRed, setFilterRed] = useState('all');
    const [filterEstado, setFilterEstado] = useState('all');
    const [filterPromocionado, setFilterPromocionado] = useState('all');
    
    // Modal states
    const [selectedDay, setSelectedDay] = useState(null);
    const [showDayModal, setShowDayModal] = useState(false);

    const monthDays = getMonthDays(currentYear, currentMonth);

    useEffect(() => {
        fetchPosts();
    }, [currentYear, currentMonth]);

    const fetchPosts = async () => {
        setLoading(true);
        try {
            // Get first and last day of the displayed calendar
            const firstDisplayed = monthDays[0].date;
            const lastDisplayed = monthDays[monthDays.length - 1].date;
            
            const response = await postsAPI.getAll({ 
                fecha_inicio: formatDate(firstDisplayed), 
                fecha_fin: formatDate(lastDisplayed) 
            });
            setPosts(response.data);
        } catch (error) {
            toast.error('Error al cargar publicaciones');
        } finally {
            setLoading(false);
        }
    };

    // Filter posts
    const filteredPosts = posts.filter(post => {
        if (filterRed !== 'all' && post.red_social !== filterRed) return false;
        if (filterEstado !== 'all' && post.estado !== filterEstado) return false;
        if (filterPromocionado !== 'all') {
            if (filterPromocionado === 'si' && !post.promocionado) return false;
            if (filterPromocionado === 'no' && post.promocionado) return false;
        }
        return true;
    });
    
    const activeFiltersCount = [filterRed, filterEstado, filterPromocionado].filter(f => f !== 'all').length;
    
    const clearFilters = () => {
        setFilterRed('all');
        setFilterEstado('all');
        setFilterPromocionado('all');
    };

    const goToPrevMonth = () => {
        if (currentMonth === 0) {
            setCurrentYear(y => y - 1);
            setCurrentMonth(11);
        } else {
            setCurrentMonth(m => m - 1);
        }
    };

    const goToNextMonth = () => {
        if (currentMonth === 11) {
            setCurrentYear(y => y + 1);
            setCurrentMonth(0);
        } else {
            setCurrentMonth(m => m + 1);
        }
    };

    const goToToday = () => {
        setCurrentYear(new Date().getFullYear());
        setCurrentMonth(new Date().getMonth());
    };

    const goToProduccion = (postId) => {
        navigate(`/produccion?highlight=${postId}`);
    };

    const postsByDate = filteredPosts.reduce((acc, post) => {
        if (!acc[post.fecha]) acc[post.fecha] = [];
        acc[post.fecha].push(post);
        return acc;
    }, {});

    const handleDayClick = (day) => {
        const dateStr = formatDate(day.date);
        const dayPosts = postsByDate[dateStr] || [];
        if (dayPosts.length > 0) {
            setSelectedDay({ date: day.date, posts: dayPosts });
            setShowDayModal(true);
        }
    };

    return (
        <div className="min-h-screen bg-slate-100">
            {/* Header */}
            <header className="bg-[#0B3D91] text-white shadow-lg">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#FF6B00] rounded flex items-center justify-center">
                                <HardHat className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="font-['Barlow_Condensed'] text-xl font-bold uppercase tracking-wide">Segumax</h1>
                                <p className="text-xs text-white/60 uppercase tracking-wider">Planner</p>
                            </div>
                        </div>
                        
                        <nav className="flex items-center gap-1">
                            <Link to="/grilla" className="flex items-center gap-2 px-4 py-2 rounded text-white/70 hover:bg-white/10 text-sm font-medium transition-colors">
                                <LayoutGrid className="w-4 h-4" />
                                <span>Semanal</span>
                            </Link>
                            <Link to="/mensual" className="flex items-center gap-2 px-4 py-2 rounded bg-white/20 text-white text-sm font-medium">
                                <CalendarDays className="w-4 h-4" />
                                <span>Mensual</span>
                            </Link>
                            <Link to="/produccion" className="flex items-center gap-2 px-4 py-2 rounded text-white/70 hover:bg-white/10 text-sm font-medium transition-colors">
                                <Clipboard className="w-4 h-4" />
                                <span>Producción</span>
                            </Link>
                        </nav>

                        <div className="flex items-center gap-4">
                            <div className="text-right">
                                <p className="text-sm font-medium">{user?.name}</p>
                                <p className="text-xs text-white/60 uppercase">{isAdmin() ? 'Administrador' : 'Cliente'}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => { logout(); window.location.href = '/login'; }} className="text-white/70 hover:text-white hover:bg-white/10">
                                <LogOut className="w-5 h-5" />
                            </Button>
                        </div>
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Month Navigation */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 mb-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <Button variant="outline" size="icon" onClick={goToPrevMonth} className="h-10 w-10" data-testid="btn-prev-month">
                                <ChevronLeft className="w-5 h-5" />
                            </Button>
                            <div className="text-center min-w-[220px]">
                                <p className="font-['Barlow_Condensed'] text-2xl font-bold text-slate-800 uppercase tracking-wide">
                                    {MESES[currentMonth]} {currentYear}
                                </p>
                            </div>
                            <Button variant="outline" size="icon" onClick={goToNextMonth} className="h-10 w-10" data-testid="btn-next-month">
                                <ChevronRight className="w-5 h-5" />
                            </Button>
                            <Button variant="outline" onClick={goToToday} className="text-sm">Hoy</Button>
                        </div>
                        
                        {isAdmin() && (
                            <Button onClick={() => navigate('/grilla')} className="bg-[#FF6B00] hover:bg-[#e65000] text-white font-semibold px-6">
                                <Plus className="w-4 h-4 mr-2" /> Nueva Publicación
                            </Button>
                        )}
                    </div>
                </div>

                {/* Filters */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-4 mb-6">
                    <div className="flex items-center gap-4 flex-wrap">
                        <div className="flex items-center gap-2">
                            <Filter className="w-4 h-4 text-slate-500" />
                            <span className="text-sm font-semibold text-slate-600 uppercase tracking-wide">Filtros:</span>
                        </div>
                        
                        <select 
                            value={filterRed} 
                            onChange={(e) => setFilterRed(e.target.value)}
                            className="h-9 px-3 border border-slate-200 rounded text-sm bg-white"
                        >
                            <option value="all">Todas las redes</option>
                            {REDES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                        
                        <select 
                            value={filterEstado} 
                            onChange={(e) => setFilterEstado(e.target.value)}
                            className="h-9 px-3 border border-slate-200 rounded text-sm bg-white"
                        >
                            <option value="all">Todos los estados</option>
                            {ESTADOS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                        </select>
                        
                        <select 
                            value={filterPromocionado} 
                            onChange={(e) => setFilterPromocionado(e.target.value)}
                            className="h-9 px-3 border border-slate-200 rounded text-sm bg-white"
                        >
                            <option value="all">Promocionado: Todos</option>
                            <option value="si">Solo ADS</option>
                            <option value="no">Sin ADS</option>
                        </select>
                        
                        {activeFiltersCount > 0 && (
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={clearFilters}
                                className="text-slate-500 hover:text-slate-700"
                            >
                                <X className="w-4 h-4 mr-1" />
                                Limpiar ({activeFiltersCount})
                            </Button>
                        )}
                        
                        <div className="ml-auto text-sm text-slate-500">
                            {filteredPosts.length} publicaciones
                        </div>
                    </div>
                </div>

                {/* Calendar Grid */}
                <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
                    {/* Days Header */}
                    <div className="grid grid-cols-7 bg-slate-50 border-b border-slate-200">
                        {DIAS_SHORT.map((dia, idx) => (
                            <div key={idx} className="px-4 py-3 text-center border-r last:border-r-0 border-slate-200">
                                <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{dia}</p>
                            </div>
                        ))}
                    </div>

                    {/* Calendar Days */}
                    {loading ? (
                        <div className="p-12 text-center">
                            <div className="w-8 h-8 border-4 border-[#0B3D91] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                            <p className="text-slate-500">Cargando...</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-7">
                            {monthDays.map((day, idx) => {
                                const dateStr = formatDate(day.date);
                                const dayPosts = postsByDate[dateStr] || [];
                                const isToday = dateStr === formatDate(new Date());
                                const maxDisplay = 3;
                                const hasMore = dayPosts.length > maxDisplay;
                                
                                return (
                                    <div 
                                        key={idx} 
                                        onClick={() => handleDayClick(day)}
                                        className={`min-h-[120px] border-r border-b last:border-r-0 border-slate-200 p-2 cursor-pointer transition-colors hover:bg-slate-50 ${
                                            !day.isCurrentMonth ? 'bg-slate-50/50' : ''
                                        } ${isToday ? 'bg-blue-50' : ''}`}
                                        data-testid={`day-${dateStr}`}
                                    >
                                        <p className={`text-sm font-bold mb-1 ${
                                            !day.isCurrentMonth ? 'text-slate-300' : 
                                            isToday ? 'text-[#0B3D91]' : 'text-slate-700'
                                        }`}>
                                            {day.date.getDate()}
                                        </p>
                                        
                                        <div className="space-y-1">
                                            {dayPosts.slice(0, maxDisplay).map((post) => {
                                                const redConfig = getRedConfig(post.red_social);
                                                return (
                                                    <div 
                                                        key={post.id}
                                                        className={`${redConfig.solid} text-white text-[10px] px-1.5 py-0.5 rounded truncate flex items-center gap-1`}
                                                        title={post.titulo}
                                                    >
                                                        {post.promocionado && <Megaphone className="w-2.5 h-2.5 flex-shrink-0" />}
                                                        <span className="truncate">{post.titulo}</span>
                                                    </div>
                                                );
                                            })}
                                            {hasMore && (
                                                <p className="text-[10px] text-slate-500 font-medium">
                                                    +{dayPosts.length - maxDisplay} más
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Legend */}
                <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-slate-500">
                    <span className="font-semibold text-slate-700">Redes:</span>
                    {REDES.map(red => (
                        <span key={red.value} className="flex items-center gap-2">
                            <span className={`${red.solid} w-4 h-4 rounded`}></span>
                            <span>{red.label}</span>
                        </span>
                    ))}
                </div>
            </main>

            {/* Day Detail Modal */}
            <Dialog open={showDayModal} onOpenChange={setShowDayModal}>
                <DialogContent className="max-w-lg">
                    {selectedDay && (
                        <>
                            <DialogHeader>
                                <DialogTitle className="font-['Barlow_Condensed'] text-xl uppercase tracking-wide">
                                    {selectedDay.date.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                                </DialogTitle>
                            </DialogHeader>
                            
                            <div className="space-y-3 mt-4 max-h-[60vh] overflow-y-auto">
                                {selectedDay.posts.map((post) => {
                                    const redConfig = getRedConfig(post.red_social);
                                    const estadoConfig = getEstadoConfig(post.estado);
                                    
                                    return (
                                        <div 
                                            key={post.id}
                                            className="border border-slate-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer"
                                            onClick={() => goToProduccion(post.id)}
                                        >
                                            <div className={`${redConfig.solid} px-3 py-1.5 flex items-center justify-between`}>
                                                <span className="text-white text-xs font-bold uppercase">{redConfig.label}</span>
                                                {post.promocionado && (
                                                    <span className="bg-amber-400 text-amber-900 px-2 py-0.5 rounded text-[10px] font-bold flex items-center gap-1">
                                                        <Megaphone className="w-3 h-3" /> ADS
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-3">
                                                <p className="font-semibold text-slate-800">{post.titulo}</p>
                                                {post.objetivo && (
                                                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                                                        <Target className="w-3 h-3" />
                                                        {post.objetivo}
                                                    </p>
                                                )}
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-xs text-slate-500 capitalize">{post.tipo}</span>
                                                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded border ${estadoConfig.color}`}>
                                                        {estadoConfig.label}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default GrillaMensualPage;
